<template>
  <div class="auth-page">
    <!-- 顶部操作栏 -->
    <div class="auth-header">
      <div class="auth-header-left">
        <span class="auth-title">权限配置</span>
        <span class="auth-sub">按模块勾选菜单、操作及数据范围权限</span>
      </div>
      <div class="auth-header-right">
        <el-button size="small" @click="onSelectAll">全部勾选</el-button>
        <el-button size="small" @click="onDeselectAll">全部清空</el-button>
        <el-button type="primary" size="small" @click="onSubmit()"
          >提交</el-button
        >
      </div>
    </div>

    <!-- 权限分组树 -->
    <div class="auth-body">
      <div
        v-for="group in state.tree"
        :key="group.title"
        class="auth-group"
        :class="{ 'is-group-collapsed': isCollapsed(group) }"
      >
        <!-- 分组标题（一级目录） -->
        <div class="group-header" @click="onToggleCollapse(group)">
          <span
            class="group-arrow"
            :class="isCollapsed(group) ? 'is-collapsed' : ''"
          ></span>
          <el-checkbox
            class="group-checkbox"
            :model-value="groupChecked(group) === 2"
            :indeterminate="groupChecked(group) === 1"
            @change="(v) => onToggleGroup(group, v)"
            @click.stop
          >
            <span class="group-title">{{ group.title }}</span>
          </el-checkbox>
          <span class="group-count"
            >{{ groupSelectedCount(group) }}/{{ groupTotalCount(group) }}</span
          >
        </div>

        <!-- 组内权限项 -->
        <div v-show="!isCollapsed(group)" class="group-body">
          <template v-for="(row, idx) in group.rows" :key="group.title + idx">
            <!-- 二级子分组标题（如"小程序管理"） -->
            <div
              v-if="row.type === 'subgroup'"
              class="subgroup-title"
              :style="{ paddingLeft: 16 + row.depth * 26 + 'px' }"
            >
              <span class="subgroup-bar"></span>
              {{ row.title }}
            </div>

            <!-- 权限项 -->
            <div
              v-else
              class="perm-row"
              :style="{ paddingLeft: 16 + row.depth * 26 + 'px' }"
            >
              <div class="perm-main">
                <el-checkbox
                  :model-value="has(mCode(row.node))"
                  @change="(v) => onCheckChange(mCode(row.node), v)"
                >
                  <span class="perm-title">{{ row.node.Title }}</span>
                </el-checkbox>
              </div>

              <!-- 操作权限 / 数据范围 -->
              <div
                v-if="row.node.BtnArr?.length || row.node.DataArr?.length"
                class="perm-sub"
              >
                <div v-if="row.node.BtnArr?.length" class="sub-line">
                  <span class="sub-label">操作</span>
                  <el-checkbox
                    v-for="btn in row.node.BtnArr"
                    :key="btn"
                    size="small"
                    :model-value="has(bCode(row.node, btn))"
                    @change="(v) => onCheckChange(bCode(row.node, btn), v)"
                  >
                    {{ btn }}
                  </el-checkbox>
                </div>
                <div v-if="row.node.DataArr?.length" class="sub-line">
                  <span class="sub-label">数据</span>
                  <el-checkbox
                    v-for="d in row.node.DataArr"
                    :key="d"
                    size="small"
                    :model-value="has(dCode(row.node, d))"
                    @change="
                      (v) => onDataChange('D_' + row.node.TableName + '_', d, v)
                    "
                  >
                    {{ d }}
                  </el-checkbox>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { onMounted, reactive } from "vue";
import { juese_getqx, juese_setqx } from "@/http/api";
import { menuTree, extraAuthItems } from "@/config/menuConfig";
import { ElMessage } from "element-plus";

// ===== 权限编码（与原逻辑保持完全一致）=====
const mCode = (n) => "M_" + n.TableName + (n.Menu ? "_" + n.Menu : "");
const bCode = (n, btn) => "B_" + n.TableName + "_" + btn;
const dCode = (n, d) => "D_" + n.TableName + "_" + d;

// 从菜单树构建"分组 -> 线性行"结构，保留层级（depth 用于缩进）
function buildAuthTree() {
  const toNode = (item) => {
    const node = {
      Title: item.title,
      TableName: item.permission || null,
      BtnArr: item.btnArr || null,
      DataArr: item.dataArr || null,
    };
    if (item.children) node.children = item.children.map(toNode);
    return node;
  };

  const buildRows = (nodes, depth, rows) => {
    nodes.forEach((n) => {
      if (n.TableName) {
        rows.push({ type: "perm", depth, node: n });
      } else if (n.children && n.children.length) {
        rows.push({ type: "subgroup", depth, title: n.Title });
        buildRows(n.children, depth + 1, rows);
      }
    });
    return rows;
  };

  const tree = [];
  menuTree.forEach((g) => {
    const rows = buildRows((g.children || []).map(toNode), 0, []);
    if (rows.length) tree.push({ title: g.title, rows });
  });

  // 独立权限项统一归入"其他独立权限"分组
  const extraRows = extraAuthItems.map((item) => ({
    type: "perm",
    depth: 0,
    node: {
      Title: item.title,
      TableName: item.permission,
      BtnArr: item.btnArr || null,
      DataArr: item.dataArr || null,
    },
  }));
  if (extraRows.length) tree.push({ title: "其他独立权限", rows: extraRows });

  return tree;
}

// 一个权限项涉及的全部编码：菜单 + 全部按钮 + 默认数据范围(第一项)
const nodeAllCodes = (n) => {
  if (!n.TableName) return [];
  const codes = [mCode(n)];
  n.BtnArr?.forEach((btn) => codes.push(bCode(n, btn)));
  if (n.DataArr?.length) codes.push(dCode(n, n.DataArr[0]));
  return codes;
};

const groupNodes = (group) =>
  group.rows.filter((r) => r.type === "perm").map((r) => r.node);

export default {
  props: {
    id: Number,
  },
  setup(props, { emit }) {
    const state = reactive({
      selectArr: [],
      tree: buildAuthTree(),
      // 默认折叠的分组（按分组标题）
      collapsedGroups: new Set(["其他独立权限"]),
    });

    const isCollapsed = (group) => state.collapsedGroups.has(group.title);

    const onToggleCollapse = (group) => {
      if (state.collapsedGroups.has(group.title)) {
        state.collapsedGroups.delete(group.title);
      } else {
        state.collapsedGroups.add(group.title);
      }
      // Set 的增删不触发响应式，替换引用强制刷新
      state.collapsedGroups = new Set(state.collapsedGroups);
    };

    onMounted(() => {
      juese_getqx(props.id).then((res) => {
        if (res.status == 200) {
          state.selectArr = res.data || [];
        }
      });
    });

    const has = (code) => state.selectArr.includes(code);

    const onCheckChange = (value, checked) => {
      if (checked) {
        if (!state.selectArr.includes(value)) state.selectArr.push(value);
      } else {
        const index = state.selectArr.findIndex((w) => w == value);
        if (index > -1) state.selectArr.splice(index, 1);
      }
    };

    // 数据范围互斥：同一权限只能选一个数据范围
    const onDataChange = (prefix, value, checked) => {
      if (checked) {
        const index = state.selectArr.findIndex((w) => w.startsWith(prefix));
        if (index > -1) state.selectArr.splice(index, 1);
        state.selectArr.push(prefix + value);
      } else {
        const index = state.selectArr.findIndex((w) => w == prefix + value);
        if (index > -1) state.selectArr.splice(index, 1);
      }
    };

    // ===== 分组级勾选 =====
    // 返回 0=未选 1=半选 2=全选
    const groupChecked = (group) => {
      const selected = groupSelectedCount(group);
      const total = groupTotalCount(group);
      if (selected === 0) return 0;
      return selected === total ? 2 : 1;
    };

    const groupSelectedCount = (group) => {
      let count = 0;
      groupNodes(group).forEach((n) => {
        nodeAllCodes(n).forEach((c) => {
          if (has(c)) count++;
        });
      });
      return count;
    };

    const groupTotalCount = (group) => {
      let count = 0;
      groupNodes(group).forEach((n) => {
        count += nodeAllCodes(n).length;
      });
      return count;
    };

    const onToggleGroup = (group, checked) => {
      groupNodes(group).forEach((n) => {
        nodeAllCodes(n).forEach((code) => onCheckChange(code, checked));
      });
    };

    const onSubmit = () => {
      juese_setqx(props.id, state.selectArr).then((res) => {
        ElMessage({
          message: res.data,
          showClose: true,
          dangerouslyUseHTMLString: true,
          type: res.status ? "success" : "error",
        });
        if (res.status == 200) {
          setTimeout(() => {
            emit("close");
          }, 500);
        }
      });
    };

    // 全选：所有菜单 + 所有按钮 + 每项默认数据范围(第一项)
    const onSelectAll = () => {
      const arr = [];
      state.tree.forEach((group) => {
        groupNodes(group).forEach((n) => arr.push(...nodeAllCodes(n)));
      });
      state.selectArr = arr;
    };

    const onDeselectAll = () => {
      state.selectArr = [];
    };

    return {
      state,
      has,
      isCollapsed,
      onToggleCollapse,
      mCode,
      bCode,
      dCode,
      onCheckChange,
      onDataChange,
      groupChecked,
      groupSelectedCount,
      groupTotalCount,
      onToggleGroup,
      onSubmit,
      onSelectAll,
      onDeselectAll,
    };
  },
};
</script>
<style scoped>
.auth-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #f5f7fa;
}

/* ===== 顶部栏 ===== */
.auth-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
}

.auth-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.auth-sub {
  margin-left: 12px;
  font-size: 12px;
  color: #909399;
}

.auth-header-right {
  display: flex;
  gap: 8px;
}

/* ===== 滚动区 ===== */
.auth-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 24px;
}

/* ===== 分组卡片 ===== */
.auth-group {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  margin-bottom: 14px;
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 20px;
  background: linear-gradient(90deg, #fdf6ec 0%, #fffaf3 100%);
  border-bottom: 1px solid #f0e8da;
  cursor: pointer;
  user-select: none;
}

.is-group-collapsed .group-header {
  border-bottom: none;
}

.group-checkbox {
  flex: 1;
  cursor: default;
}

.group-arrow {
  display: inline-block;
  width: 0;
  height: 0;
  margin-right: 12px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 7px solid #c0a373;
  transition: transform 0.2s ease;
  transform: rotate(0deg);
}

.group-arrow.is-collapsed {
  transform: rotate(-90deg);
}

.group-title {
  font-size: 16px;
  font-weight: 700;
  color: #b88230;
  letter-spacing: 1px;
}

.group-count {
  font-size: 12px;
  color: #c0a373;
  background: #fff;
  border: 1px solid #ecd9b8;
  border-radius: 10px;
  padding: 1px 10px;
}

.group-body {
  margin: 4px 18px 4px 22px;
  padding: 4px 0 4px 22px;
  border-left: 2px solid #f0e8da;
}

/* ===== 二级子分组 ===== */
.subgroup-title {
  display: flex;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.subgroup-bar {
  display: inline-block;
  width: 4px;
  height: 14px;
  background: #e4870d;
  border-radius: 2px;
  margin-right: 8px;
}

/* ===== 权限项 ===== */
.perm-row {
  padding-top: 10px;
  padding-bottom: 10px;
  padding-right: 8px;
  border-bottom: 1px dashed #f2f3f5;
}

.perm-row:last-child {
  border-bottom: none;
}

.perm-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

/* 操作/数据权限：左侧竖线引导，体现从属层级（三级） */
.perm-sub {
  margin-top: 8px;
  margin-left: 28px;
  padding: 8px 12px 2px;
  border-left: 2px solid #ecd9b8;
  background: #fcfaf7;
  border-radius: 0 6px 6px 0;
}

.sub-line {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 14px;
  padding-bottom: 8px;
}

.sub-label {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
  background: #f0e8da;
  border-radius: 4px;
  padding: 1px 8px;
  margin-right: 4px;
}
</style>
